import {Popover} from '@mui/material'
import {alpha, type PaletteColor} from '@mui/material/styles'
import React, {PropsWithChildren} from 'react'
import {TooltipStyled} from '../tooltipCustom'
import Icon, {type TIconName} from '../icon'
import {theme} from '../../theme'

type PopoverButtonProps = {
  buttonIcon?: TIconName
  buttonIconSize?: string
  buttonText?: string
  buttonClassName: string
  buttonColor?: PaletteColor
  buttonVariant?: 'filled' | 'outlined'
  tooltip?: string
  isActive?: boolean
  /** Which button edge the popover aligns to. Defaults to 'right'. */
  align?: 'left' | 'right'
}

export default function PopoverButton({
  buttonIcon,
  buttonIconSize = '24px',
  buttonText,
  buttonClassName,
  buttonColor,
  buttonVariant = 'filled',
  children,
  tooltip,
  isActive,
  align = 'right',
}: PropsWithChildren<PopoverButtonProps>) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null)
  const [hover, setHover] = React.useState(false)

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)

  const isOutlined = Boolean(buttonColor) && buttonVariant === 'outlined'

  const resolvedBgColor = (() => {
    if (isActive) return theme.palette.action.selected
    if (isOutlined && buttonColor)
      return hover || open
        ? alpha(buttonColor.main, 0.08)
        : theme.palette.background.paper
    if (buttonColor) return hover ? buttonColor.dark : buttonColor.main
    return theme.palette.background.paper
  })()

  const resolvedContentColor = (() => {
    if (isActive) return theme.palette.primary.main
    if (isOutlined && buttonColor) return buttonColor.main
    if (buttonColor) return buttonColor.contrastText
    return open ? theme.palette.primary.main : theme.palette.text.primary
  })()

  const resolvedBorder = (() => {
    if (isOutlined && buttonColor) return `1px solid ${buttonColor.main}`
    if (open) return `1px solid ${theme.palette.primary.main}`
    if (buttonColor) return '1px solid'
    return `1px solid ${theme.palette.divider}`
  })()

  return (
    <div>
      <TooltipStyled title={tooltip} placement='bottom'>
        <div
          className={buttonClassName}
          style={{
            border: resolvedBorder,
            backgroundColor: resolvedBgColor,
          }}
          onClick={handleClick}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}>
          {buttonIcon && (
            <Icon
              type={buttonIcon}
              size={buttonIconSize}
              color={resolvedContentColor}
            />
          )}
          {buttonText && (
            <span style={{color: resolvedContentColor}}>{buttonText}</span>
          )}
        </div>
      </TooltipStyled>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        transformOrigin={{
          vertical: 'top',
          horizontal: align,
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: align,
        }}
        sx={{
          '& .MuiPopover-paper': {
            borderRadius: '10px',
            boxShadow: `0px 4px 4px ${theme.palette.action.focus}`,
            padding: '12px',
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
            backgroundColor: theme.palette.background.default,
            maxHeight: '400px',
            overflowY: 'auto',
          },
        }}>
        <div onClick={handleClose}>{children}</div>
      </Popover>
    </div>
  )
}
